import { BooleanExpr } from '../survey';
import type { FieldEntryDataType } from './settings';

// Generalized filtering types that can be used across reports, dashboards, and other features

// Filter operation types that map to BooleanExpr operations
export type FilterOperator =
  | 'equals'
  | 'not_equals'
  | 'contains'
  | 'not_contains'
  | 'greater_than'
  | 'less_than'
  | 'after'
  | 'before'
  | 'last_modified'
  | 'last_modified_before'
  | 'last_modified_after'
  | 'exists'
  | 'not_exists';

/**
 * The 'empty'/'not_empty' operators were folded into 'not_exists'/'exists' (which now
 * treat blank the same as missing). Old saved filters may still carry the legacy strings,
 * so anything that branches on a persisted operator runs it through this first:
 *   'empty' -> 'not_exists' (missing or blank), 'not_empty' -> 'exists' (present, non-blank)
 */
export function normalizeLegacyOperator(operator: string): FilterOperator {
  if (operator === 'empty') return 'not_exists';
  if (operator === 'not_empty') return 'exists';
  return operator as FilterOperator;
}

export type { FieldEntryDataType };

export type FieldOption = {
  key: string;
  label: string;
  type: FieldEntryDataType;
}

/**
 * The synthetic 'applicant_group' field definition.
 * This special field allows users to reference applicant groups within filter panels.
 */
export const APPLICANT_GROUP_FIELD: FieldOption = {
  key: 'applicant_group',
  label: 'Applicant Group',
  type: 'text'
};

/**
 * The synthetic 'Applicant UID' field key used throughout filter panels.
 * The client treats this like a normal target field; the backend translates it to `uid`.
 */
export const SYNTHETIC_UID_FIELD_KEY = '__synthetic_uid__';

/**
 * Operators offered for the synthetic Applicant UID field. A UID is an opaque
 * identifier, so range comparisons (greater_than/less_than), existence checks
 * (exists/not_exists), and last-modified comparisons don't make sense for it.
 */
export const SYNTHETIC_UID_OPERATORS: FilterOperator[] = ['equals', 'not_equals', 'contains', 'not_contains'];

export type FilterLogic = 'and' | 'or';

// Basic filter condition for simple operations
export type FilterCondition = {
  field: string; // Target field name
  operator: FilterOperator;
  value?: string; // For operators that need a value
  // For last_modified operator
  ago?: { amount: number; unit: 'days' | 'weeks' | 'months' };
  /** When true, the condition is displayed as a non-editable, non-deletable summary.
   *  Used for BooleanExpr nodes that have no editable FilterCondition equivalent. */
  readOnly?: boolean;
  /** Human-readable summary shown in the UI for read-only conditions. */
  description?: string;
  /** The original BooleanExpr preserved for lossless round-tripping. */
  rawBooleanExpr?: Record<string, unknown>;
}

// Filter group that can contain conditions and sub-groups
export type FilterGroup = {
  logic: FilterLogic;
  conditions: Array<FilterCondition | FilterGroup>;
}

// Complete filter configuration - generalized from ReportFilterConfig
export type FilterConfig = {
  enabled: boolean;
  config?: FilterGroup;
}

// UI Filter state for the frontend
export type UIFilterState = {
  activeFilters: FilterCondition[];
  filterGroups: FilterGroup[];
}

// Sorting and grouping types
export type SortDirection = 'asc' | 'desc';

export type GroupByRule = {
  field: string;
  direction: SortDirection;
}

export type GroupByConfig = {
  enabled: boolean;
  rules?: GroupByRule[];
};

// Legacy type aliases for backward compatibility
export type ReportFilterConfig = FilterConfig;
export type ApplicantGroupFilterConfig = FilterConfig;

/**
 * Evaluates a FilterConfig against a flat data row.
 * Shared between frontend (client-side filtering) and backend (application-level filtering).
 */
export function evaluateFilterConfig(
  row: Record<string, string | undefined>,
  filterConfig: FilterConfig
): boolean {
  if (!filterConfig.enabled || !filterConfig.config) return true;
  return evaluateGroup(row, filterConfig.config);
}

function evaluateGroup(
  row: Record<string, string | undefined>,
  group: FilterGroup
): boolean {
  if (!group.conditions || group.conditions.length === 0) return true;

  if (group.logic === 'and') {
    return group.conditions.every(condition => evaluateConditionOrGroup(row, condition));
  }
  return group.conditions.some(condition => evaluateConditionOrGroup(row, condition));
}

function evaluateConditionOrGroup(
  row: Record<string, string | undefined>,
  item: FilterCondition | FilterGroup
): boolean {
  if ('logic' in item && 'conditions' in item) {
    return evaluateGroup(row, item as FilterGroup);
  }
  return evaluateCondition(row, item as FilterCondition);
}

// Date matching is day-granular and format-agnostic. Row values are typically ISO
// ("2025-09-01" or "2025-09-01T00:00:00Z"), while the filter date picker emits US
// format ("09/01/2025"). We extract the calendar day from the string directly rather
// than via `new Date()`, because ISO date-only parses as UTC midnight while
// "MM/DD/YYYY" parses as *local* midnight — round-tripping through toISOString() can
// shift the day by one depending on the viewer's timezone.

const ISO_DAY = /^(\d{4})-(\d{2})-(\d{2})/;        // 2025-09-01 / 2025-09-01T...
const US_DAY = /^(\d{1,2})\/(\d{1,2})\/(\d{4})/;   // 9/1/2025 / 09/01/2025

/** Whether a string looks like a date we can compare at day granularity (ISO or US). */
export function isDateLike(s: string): boolean {
  return ISO_DAY.test(s) || US_DAY.test(s);
}

/** Extract the calendar day as a sortable 'YYYY-MM-DD' from an ISO or US date string. */
export function toDay(s: string): string | undefined {
  const iso = ISO_DAY.exec(s);
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;
  const us = US_DAY.exec(s);
  if (us) return `${us[3]}-${us[1].padStart(2, '0')}-${us[2].padStart(2, '0')}`;
  return undefined;
}

/** True when both strings resolve to the same calendar day. */
function isSameDay(a: string, b: string): boolean {
  const dayA = toDay(a);
  return dayA !== undefined && dayA === toDay(b);
}

function evaluateCondition(
  row: Record<string, string | undefined>,
  condition: FilterCondition
): boolean {
  const value = row[condition.field] ?? '';
  const conditionValue = condition.value ?? '';

  switch (normalizeLegacyOperator(condition.operator)) {
    case 'equals':
      // For date values, "is" means "within the same day" rather than an exact
      // timestamp string match (e.g. "2025-03-15T00:00:00Z" matches "2025-03-15").
      if (isDateLike(value) && isDateLike(conditionValue)) {
        return isSameDay(value, conditionValue);
      }
      return value.toLowerCase() === conditionValue.toLowerCase();
    case 'not_equals':
      if (isDateLike(value) && isDateLike(conditionValue)) {
        return !isSameDay(value, conditionValue);
      }
      return value.toLowerCase() !== conditionValue.toLowerCase();
    case 'contains':
      return value.toLowerCase().includes(conditionValue.toLowerCase());
    case 'not_contains':
      return !value.toLowerCase().includes(conditionValue.toLowerCase());
    case 'greater_than':
      return Number(value) > Number(conditionValue);
    case 'less_than':
      return Number(value) < Number(conditionValue);
    case 'after':
      // Day-granular when both look like dates (handles ISO vs US format); 'YYYY-MM-DD'
      // strings sort chronologically. Falls back to raw Date parsing otherwise.
      if (isDateLike(value) && isDateLike(conditionValue)) {
        const a = toDay(value), b = toDay(conditionValue);
        return a !== undefined && b !== undefined && a > b;
      }
      return new Date(value) > new Date(conditionValue);
    case 'before':
      if (isDateLike(value) && isDateLike(conditionValue)) {
        const a = toDay(value), b = toDay(conditionValue);
        return a !== undefined && b !== undefined && a < b;
      }
      return new Date(value) < new Date(conditionValue);
    case 'exists':
      return value !== '';
    case 'not_exists':
      return value === '';
    case 'last_modified':
    case 'last_modified_before':
    case 'last_modified_after':
      return true; // Requires timestamp metadata, not supported in flat row evaluation
    default:
      return true;
  }
}

// ---------------------------------------------------------------------------
// FilterGroup <-> BooleanExpr converters
// ---------------------------------------------------------------------------

/**
 * Converts FilterConfig (or FilterGroup) to BooleanExpr format.
 * Single source of truth used by both frontend and backend.
 */
export class FilterGroupToBooleanExprConverter {

  static convertFromConfig(filterConfig: FilterConfig): BooleanExpr | undefined {
    if (!filterConfig.enabled || !filterConfig.config) {
      return undefined;
    }
    return FilterGroupToBooleanExprConverter.convert(filterConfig.config);
  }

  static convert(group: FilterGroup): BooleanExpr {
    const clauses: BooleanExpr[] = [];

    for (const condition of group.conditions) {
      if ('field' in condition) {
        const booleanExpr = FilterGroupToBooleanExprConverter.convertCondition(condition);
        if (booleanExpr) {
          clauses.push(booleanExpr);
        }
      } else {
        clauses.push(FilterGroupToBooleanExprConverter.convert(condition));
      }
    }

    if (clauses.length === 0) {
      return { kind: 'Not', clause: { kind: 'Never' } };
    }

    if (clauses.length === 1) {
      return clauses[0];
    }

    return group.logic === 'and'
      ? { kind: 'And', clauses }
      : { kind: 'Or', clauses };
  }

  private static convertCondition(condition: FilterCondition): BooleanExpr | null {
    if (condition.readOnly && condition.rawBooleanExpr) {
      return condition.rawBooleanExpr as unknown as BooleanExpr;
    }

    const { field, value } = condition;
    const operator = normalizeLegacyOperator(condition.operator);

    if (!field || field.trim() === '') {
      return null;
    }

    switch (operator) {
      case 'equals':
        if (value == null) return null;
        return {
          kind: 'Equals',
          field,
          value
        };

      case 'not_equals':
        if (value == null) return null;
        return {
          kind: 'Not Equal',
          field,
          value
        };

      case 'contains':
        if (value == null) return null;
        return {
          kind: 'Value Contains',
          mode: 'string',
          caseInsensitive: true,
          value: { kind: 'Field', field },
          contains: { kind: 'StringValue', value }
        };

      case 'not_contains':
        if (value == null) return null;
        return {
          kind: 'Not',
          clause: {
            kind: 'Value Contains',
            mode: 'string',
            caseInsensitive: true,
            value: { kind: 'Field', field },
            contains: { kind: 'StringValue', value }
          }
        };

      case 'greater_than':
        if (value == null) return null;
        return {
          kind: 'Value Greater Than',
          value: { kind: 'Field', field },
          greaterThan: { kind: 'NumericValue', value: Number(value) }
        };

      case 'less_than':
        if (value == null) return null;
        return {
          kind: 'Value Less Than',
          value: { kind: 'Field', field },
          lessThan: { kind: 'NumericValue', value: Number(value) }
        };

      case 'after':
        if (value == null || value.trim() === '') return null;
        return {
          kind: 'Value Greater Than',
          value: { kind: 'As Date', value: { kind: 'Field', field } },
          greaterThan: { kind: 'As Date', value: { kind: 'StringValue', value } }
        };

      case 'before':
        if (value == null || value.trim() === '') return null;
        return {
          kind: 'Value Less Than',
          value: { kind: 'As Date', value: { kind: 'Field', field } },
          lessThan: { kind: 'As Date', value: { kind: 'StringValue', value } }
        };

      case 'exists':
        return { kind: 'Exists', field };

      case 'not_exists':
        return { kind: 'DoesntExist', field };

      case 'last_modified':
        if (!condition.ago) return null;
        return {
          kind: 'Last Modified',
          field,
          ago: { amount: condition.ago.amount, unit: condition.ago.unit }
        };

      case 'last_modified_before':
        if (value == null || value.trim() === '') return null;
        {
          const daysDiff = Math.floor((Date.now() - new Date(value).getTime()) / (1000 * 60 * 60 * 24));
          return {
            kind: 'Last Modified',
            field,
            ago: { amount: daysDiff, unit: 'days' as const }
          };
        }

      case 'last_modified_after':
        if (value == null || value.trim() === '') return null;
        {
          // "Last modified after <date>" is the negation of "Last Modified" (which means
          // last modified more than `ago` ago), i.e. last modified within the last `daysDiff` days.
          const daysDiff = Math.floor((Date.now() - new Date(value).getTime()) / (1000 * 60 * 60 * 24));
          return {
            kind: 'Not',
            clause: {
              kind: 'Last Modified',
              field,
              ago: { amount: daysDiff, unit: 'days' as const }
            }
          };
        }

      default:
        console.warn(`Unknown filter operator: ${operator}`);
        return null;
    }
  }
}

/**
 * Reverse converter — BooleanExpr back to FilterGroup format.
 * Used when initializing FilterPanel with existing filters from the backend.
 */
export class BooleanExprToFilterGroupConverter {

  static convert(booleanExpr: BooleanExpr | any): FilterGroup | undefined {
    if (!booleanExpr || typeof booleanExpr !== 'object') return undefined;

    // Treat the "match all"/no-op sentinels (Never, and the common Not(Never)
    // used for default report and applicant-group filters) as "no admin filter"
    // rather than rendering a locked read-only panel row.
    if (BooleanExprToFilterGroupConverter.isNoOpExpr(booleanExpr)) {
      return undefined;
    }

    const { kind } = booleanExpr;

    if (kind === 'And' || kind === 'Or') {
      const logic = kind.toLowerCase() as 'and' | 'or';
      const conditions: Array<FilterCondition | FilterGroup> = [];

      if (booleanExpr.clauses && Array.isArray(booleanExpr.clauses)) {
        for (const clause of booleanExpr.clauses) {
          const condition = BooleanExprToFilterGroupConverter.convertToCondition(clause);
          if (condition) {
            conditions.push(condition);
          } else {
            const group = BooleanExprToFilterGroupConverter.convert(clause);
            if (group) {
              conditions.push(group);
            }
          }
        }
      }

      return conditions.length > 0 ? { logic, conditions } : undefined;
    }

    const condition = BooleanExprToFilterGroupConverter.convertToCondition(booleanExpr);
    if (condition) {
      return { logic: 'and', conditions: [condition] };
    }

    return undefined;
  }

  private static convertToCondition(booleanExpr: any): FilterCondition | undefined {
    if (!booleanExpr || typeof booleanExpr !== 'object') return undefined;

    const { kind } = booleanExpr;

    if (kind === 'And' || kind === 'Or') return undefined;

    // No-op sentinels (Never / Not(Never)) carry no filter and shouldn't render.
    if (BooleanExprToFilterGroupConverter.isNoOpExpr(booleanExpr)) return undefined;

    if (booleanExpr.field) {
      const condition: FilterCondition = {
        field: booleanExpr.field,
        operator: 'equals',
        value: undefined
      };

      switch (kind) {
        case 'Exists':
          condition.operator = 'exists';
          break;
        case 'DoesntExist':
          condition.operator = 'not_exists';
          break;
        case 'Equals':
          condition.operator = 'equals';
          condition.value = booleanExpr.value;
          break;
        case 'Not Equal':
          condition.operator = 'not_equals';
          condition.value = booleanExpr.value;
          break;
        case 'Last Modified':
          if (booleanExpr.ago) {
            const unitToMs: Record<string, number> = {
              'days': 1000 * 60 * 60 * 24,
              'weeks': 1000 * 60 * 60 * 24 * 7,
              'months': 1000 * 60 * 60 * 24 * 30,
            };
            const ms = booleanExpr.ago.amount * (unitToMs[booleanExpr.ago.unit] || unitToMs['days']);
            const date = new Date(Date.now() - ms);
            const dateStr = `${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getDate()).padStart(2, '0')}/${date.getFullYear()}`;
            condition.operator = 'last_modified_before';
            condition.value = dateStr;
          }
          break;
        default:
          return BooleanExprToFilterGroupConverter.createReadOnlyCondition(booleanExpr);
      }

      return condition;
    }

    let fieldName: string | undefined;

    if (booleanExpr.value && booleanExpr.value.kind === 'Field') {
      fieldName = booleanExpr.value.field;
    } else if (booleanExpr.value && booleanExpr.value.kind === 'As Date' && booleanExpr.value.value && booleanExpr.value.value.kind === 'Field') {
      fieldName = booleanExpr.value.value.field;
    } else if (booleanExpr.value && booleanExpr.value.kind === 'Last Modified Date') {
      fieldName = booleanExpr.value.field;
    }

    if (fieldName) {
      const condition: FilterCondition = {
        field: fieldName,
        operator: 'equals',
        value: undefined
      };

      switch (kind) {
        case 'Value Equals':
          if (booleanExpr.equals?.kind !== 'StringValue') {
            return BooleanExprToFilterGroupConverter.createReadOnlyCondition(booleanExpr);
          }
          condition.operator = 'equals';
          condition.value = String(booleanExpr.equals.value ?? '');
          break;
        case 'Value Not Equal':
          if (booleanExpr.notEqual?.kind !== 'StringValue') {
            return BooleanExprToFilterGroupConverter.createReadOnlyCondition(booleanExpr);
          }
          condition.operator = 'not_equals';
          condition.value = String(booleanExpr.notEqual.value ?? '');
          break;
        case 'Value Contains':
          if (booleanExpr.contains?.kind !== 'StringValue') {
            return BooleanExprToFilterGroupConverter.createReadOnlyCondition(booleanExpr);
          }
          condition.operator = 'contains';
          condition.value = String(booleanExpr.contains.value ?? '');
          break;
        // Legacy empty/not_empty BooleanExprs map onto exists/not_exists, which now
        // treat blank values the same way (exists = present & non-blank).
        case 'Value Empty':
          condition.operator = 'not_exists';
          break;
        case 'Value Not Empty':
          condition.operator = 'exists';
          break;
        case 'Value Greater Than':
          if (booleanExpr.greaterThan?.kind === 'As Date') {
            condition.operator = 'after';
            condition.value = String(booleanExpr.greaterThan?.value?.value ?? '');
          } else if (booleanExpr.greaterThan?.kind === 'NumericValue') {
            condition.operator = 'greater_than';
            condition.value = String(booleanExpr.greaterThan?.value ?? '');
          } else {
            return BooleanExprToFilterGroupConverter.createReadOnlyCondition(booleanExpr);
          }
          break;
        case 'Value Less Than':
          if (booleanExpr.lessThan?.kind === 'As Date') {
            condition.operator = 'before';
            condition.value = String(booleanExpr.lessThan?.value?.value ?? '');
          } else if (booleanExpr.lessThan?.kind === 'NumericValue') {
            condition.operator = 'less_than';
            condition.value = String(booleanExpr.lessThan?.value ?? '');
          } else {
            return BooleanExprToFilterGroupConverter.createReadOnlyCondition(booleanExpr);
          }
          break;
        default:
          return BooleanExprToFilterGroupConverter.createReadOnlyCondition(booleanExpr);
      }

      return condition;
    }

    if (kind === 'Not' && booleanExpr.clause) {
      const inner = BooleanExprToFilterGroupConverter.convertToCondition(booleanExpr.clause);
      if (inner && !inner.readOnly) {
        const negated = BooleanExprToFilterGroupConverter.negateOperator(inner.operator);
        if (negated) {
          return { ...inner, operator: negated };
        }
      }
      return BooleanExprToFilterGroupConverter.createReadOnlyCondition(booleanExpr);
    }

    return BooleanExprToFilterGroupConverter.createReadOnlyCondition(booleanExpr);
  }

  /**
   * Detects "no filter" sentinels: a bare `Never`, or a `Not` wrapping `Never`
   * (the canonical "match all" expression). These should map to no admin filters
   * rather than a locked read-only condition.
   */
  private static isNoOpExpr(booleanExpr: any): boolean {
    if (!booleanExpr || typeof booleanExpr !== 'object') return false;
    if (booleanExpr.kind === 'Never') return true;
    if (booleanExpr.kind === 'Not') {
      console.log('isNoOpExpr', booleanExpr.clause);
      return BooleanExprToFilterGroupConverter.isNoOpExpr(booleanExpr.clause);
    }
    return false;
  }

  private static createReadOnlyCondition(booleanExpr: any): FilterCondition {
    return {
      field: BooleanExprToFilterGroupConverter.extractFieldName(booleanExpr) ?? '',
      operator: 'equals',
      readOnly: true,
      description: BooleanExprToFilterGroupConverter.describeBooleanExpr(booleanExpr),
      rawBooleanExpr: booleanExpr as Record<string, unknown>,
    };
  }

  private static extractFieldName(expr: any): string | undefined {
    if (!expr || typeof expr !== 'object') return undefined;
    if (expr.field && typeof expr.field === 'string') return expr.field;
    if (expr.value?.kind === 'Field') return expr.value.field;
    if (expr.value?.kind === 'As Date' && expr.value.value?.kind === 'Field') return expr.value.value.field;
    if (expr.value?.kind === 'Last Modified Date') return expr.value.field;
    if (expr.clause) return BooleanExprToFilterGroupConverter.extractFieldName(expr.clause);
    return undefined;
  }

  private static describeValueExpr(expr: any): string {
    if (!expr || typeof expr !== 'object') return String(expr ?? '');
    switch (expr.kind) {
      case 'Field': return expr.field;
      case 'StringValue': return `"${expr.value}"`;
      case 'NumericValue': return String(expr.value);
      case 'As Date': return `date(${BooleanExprToFilterGroupConverter.describeValueExpr(expr.value)})`;
      case 'Last Modified Date': return `last_modified(${expr.field})`;
      case 'Add': return `(${BooleanExprToFilterGroupConverter.describeValueExpr(expr.baseValue)} + ${BooleanExprToFilterGroupConverter.describeValueExpr(expr.secondValue)})`;
      case 'Subtract': return `(${BooleanExprToFilterGroupConverter.describeValueExpr(expr.baseValue)} - ${BooleanExprToFilterGroupConverter.describeValueExpr(expr.secondValue)})`;
      case 'Multiply': return `(${BooleanExprToFilterGroupConverter.describeValueExpr(expr.baseValue)} * ${BooleanExprToFilterGroupConverter.describeValueExpr(expr.secondValue)})`;
      case 'Divide': return `(${BooleanExprToFilterGroupConverter.describeValueExpr(expr.baseValue)} / ${BooleanExprToFilterGroupConverter.describeValueExpr(expr.secondValue)})`;
      default: return expr.kind ?? JSON.stringify(expr);
    }
  }

  private static describeBooleanExpr(expr: any): string {
    if (!expr || typeof expr !== 'object') return String(expr ?? '');
    const lhs = expr.value ? BooleanExprToFilterGroupConverter.describeValueExpr(expr.value) : undefined;
    switch (expr.kind) {
      case 'Value Equals': return `${lhs} = ${BooleanExprToFilterGroupConverter.describeValueExpr(expr.equals)}`;
      case 'Value Not Equal': return `${lhs} ≠ ${BooleanExprToFilterGroupConverter.describeValueExpr(expr.notEqual)}`;
      case 'Value Greater Than': return `${lhs} > ${BooleanExprToFilterGroupConverter.describeValueExpr(expr.greaterThan)}`;
      case 'Value Less Than': return `${lhs} < ${BooleanExprToFilterGroupConverter.describeValueExpr(expr.lessThan)}`;
      case 'Value Contains': return `${lhs} contains ${BooleanExprToFilterGroupConverter.describeValueExpr(expr.contains)}`;
      case 'Value Empty': return `${lhs} is empty`;
      case 'Value Not Empty': return `${lhs} is not empty`;
      case 'Exists': return `${expr.field} exists`;
      case 'DoesntExist': return `${expr.field} doesn't exist`;
      case 'Not': return `NOT (${BooleanExprToFilterGroupConverter.describeBooleanExpr(expr.clause)})`;
      case 'And': return (expr.clauses || []).map((c: any) => BooleanExprToFilterGroupConverter.describeBooleanExpr(c)).join(' AND ');
      case 'Or': return (expr.clauses || []).map((c: any) => BooleanExprToFilterGroupConverter.describeBooleanExpr(c)).join(' OR ');
      case 'Last Modified': return `${expr.field} last modified ${expr.ago?.amount} ${expr.ago?.unit} ago`;
      default: return expr.kind ?? JSON.stringify(expr);
    }
  }

  private static negateOperator(op: FilterCondition['operator']): FilterCondition['operator'] | null {
    const negationMap: Partial<Record<FilterCondition['operator'], FilterCondition['operator']>> = {
      'equals': 'not_equals',
      'not_equals': 'equals',
      'contains': 'not_contains',
      'not_contains': 'contains',
      'exists': 'not_exists',
      'not_exists': 'exists',
      'last_modified_before': 'last_modified_after',
      'last_modified_after': 'last_modified_before',
    };
    return negationMap[op] ?? null;
  }
}